import React, { useState } from 'react';
import './ApiLogs.css';

const ApiLogs = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('24h');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Mock API logs data
  const [apiLogs] = useState([
    {
      id: 1,
      timestamp: '2024-01-21T14:25:30',
      endpoint: '/v18.0/123456789012345/messages',
      method: 'POST',
      status: 'success',
      statusCode: 200,
      responseTime: 145,
      requestSize: 1024,
      responseSize: 512,
      userId: 'user_123',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0...',
      requestBody: { messaging_product: 'whatsapp', to: '+1234567890', type: 'text' },
      responseBody: { messaging_product: 'whatsapp', contacts: [], messages: [] }
    },
    {
      id: 2,
      timestamp: '2024-01-21T14:24:15',
      endpoint: '/v18.0/123456789012345/message_templates',
      method: 'GET',
      status: 'success',
      statusCode: 200,
      responseTime: 89,
      requestSize: 256,
      responseSize: 2048,
      userId: 'user_123',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0...',
      requestBody: null,
      responseBody: { data: [] }
    },
    {
      id: 3,
      timestamp: '2024-01-21T14:23:00',
      endpoint: '/v18.0/123456789012345/messages',
      method: 'POST',
      status: 'error',
      statusCode: 400,
      responseTime: 23,
      requestSize: 512,
      responseSize: 128,
      userId: 'user_456',
      ipAddress: '192.168.1.101',
      userAgent: 'Mozilla/5.0...',
      requestBody: { messaging_product: 'whatsapp', to: 'invalid', type: 'text' },
      responseBody: { error: { message: 'Invalid phone number', type: 'OAuthException' } }
    },
    {
      id: 4,
      timestamp: '2024-01-21T14:22:45',
      endpoint: '/v18.0/123456789012345/phone_numbers',
      method: 'GET',
      status: 'success',
      statusCode: 200,
      responseTime: 67,
      requestSize: 128,
      responseSize: 1536,
      userId: 'user_123',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0...',
      requestBody: null,
      responseBody: { data: [] }
    },
    {
      id: 5,
      timestamp: '2024-01-21T14:21:30',
      endpoint: '/v18.0/123456789012345/messages',
      method: 'POST',
      status: 'error',
      statusCode: 429,
      responseTime: 12,
      requestSize: 768,
      responseSize: 256,
      userId: 'user_789',
      ipAddress: '192.168.1.102',
      userAgent: 'Mozilla/5.0...',
      requestBody: { messaging_product: 'whatsapp', to: '+1234567890', type: 'text' },
      responseBody: { error: { message: 'Rate limit exceeded', type: 'OAuthException' } }
    }
  ]);

  const statuses = ['all', 'success', 'error', 'warning'];

  const filteredLogs = apiLogs.filter(log => {
    const matchesSearch = 
      log.endpoint.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.method.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.statusCode.toString().includes(searchQuery);
    
    const matchesStatus = 
      selectedStatus === 'all' || log.status === selectedStatus;

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const config = {
      'success': { color: '#10b981', bg: '#d1fae5' },
      'error': { color: '#ef4444', bg: '#fee2e2' },
      'warning': { color: '#f59e0b', bg: '#fef3c7' }
    };

    const statusConfig = config[status] || config['success'];
    return (
      <span 
        className="status-badge" 
        style={{ background: statusConfig.bg, color: statusConfig.color }}
      >
        {status.toUpperCase()}
      </span>
    );
  };

  const getStatusCodeBadge = (code) => {
    if (code >= 200 && code < 300) {
      return <span className="status-code success">{code}</span>;
    } else if (code >= 400 && code < 500) {
      return <span className="status-code error">{code}</span>;
    } else if (code >= 500) {
      return <span className="status-code server-error">{code}</span>;
    }
    return <span className="status-code">{code}</span>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const stats = {
    total: apiLogs.length,
    success: apiLogs.filter(l => l.status === 'success').length,
    errors: apiLogs.filter(l => l.status === 'error').length,
    avgResponseTime: Math.round(apiLogs.reduce((sum, l) => sum + l.responseTime, 0) / apiLogs.length),
    totalRequests: apiLogs.length
  };

  const [selectedLog, setSelectedLog] = useState(null);

  return (
    <div className="api-logs-container">
      <div className="api-logs-header">
        <div className="header-content">
          <h1>API Logs & Audit Trail</h1>
          <p>Monitor API requests, responses, and system activity</p>
        </div>
        <div className="period-selector">
          <button
            className={`period-btn ${selectedPeriod === '24h' ? 'active' : ''}`}
            onClick={() => setSelectedPeriod('24h')}
          >
            24 Hours
          </button>
          <button
            className={`period-btn ${selectedPeriod === '7d' ? 'active' : ''}`}
            onClick={() => setSelectedPeriod('7d')}
          >
            7 Days
          </button>
          <button
            className={`period-btn ${selectedPeriod === '30d' ? 'active' : ''}`}
            onClick={() => setSelectedPeriod('30d')}
          >
            30 Days
          </button>
        </div>
      </div>

      <div className="api-logs-stats">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total Requests</div>
        </div>
        <div className="stat-card success">
          <div className="stat-value">{stats.success}</div>
          <div className="stat-label">Successful</div>
        </div>
        <div className="stat-card error">
          <div className="stat-value">{stats.errors}</div>
          <div className="stat-label">Errors</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.avgResponseTime}ms</div>
          <div className="stat-label">Avg Response Time</div>
        </div>
      </div>

      <div className="api-logs-toolbar">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search by endpoint, method, or status code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="filter-select"
          >
            {statuses.map(status => (
              <option key={status} value={status}>
                {status === 'all' ? 'All Status' : status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="api-logs-table-container">
        <table className="api-logs-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Method</th>
              <th>Endpoint</th>
              <th>Status</th>
              <th>Status Code</th>
              <th>Response Time</th>
              <th>User</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.length === 0 ? (
              <tr>
                <td colSpan="8" className="empty-state-cell">
                  <div className="empty-state">
                    <div className="empty-icon">📋</div>
                    <h3>No API logs found</h3>
                    <p>API requests will appear here</p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredLogs.map(log => (
                <tr key={log.id} className={log.status === 'error' ? 'error-row' : ''}>
                  <td>{formatDate(log.timestamp)}</td>
                  <td>
                    <span className={`method-badge ${log.method.toLowerCase()}`}>
                      {log.method}
                    </span>
                  </td>
                  <td>
                    <span className="endpoint-value">{log.endpoint}</span>
                  </td>
                  <td>{getStatusBadge(log.status)}</td>
                  <td>{getStatusCodeBadge(log.statusCode)}</td>
                  <td>
                    <span className={`response-time ${log.responseTime > 1000 ? 'slow' : log.responseTime > 500 ? 'medium' : 'fast'}`}>
                      {log.responseTime}ms
                    </span>
                  </td>
                  <td>
                    <span className="user-id">{log.userId}</span>
                  </td>
                  <td>
                    <button
                      className="btn-view-details"
                      onClick={() => setSelectedLog(selectedLog?.id === log.id ? null : log)}
                    >
                      {selectedLog?.id === log.id ? 'Hide' : 'View'} Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedLog && (
        <div className="log-details-modal">
          <div className="log-details-header">
            <h3>Request Details</h3>
            <button
              className="btn-close-details"
              onClick={() => setSelectedLog(null)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div className="log-details-body">
            <div className="detail-section">
              <h4>Request</h4>
              <div className="detail-item">
                <span className="detail-label">Method:</span>
                <span className="detail-value">{selectedLog.method}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Endpoint:</span>
                <span className="detail-value">{selectedLog.endpoint}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">IP Address:</span>
                <span className="detail-value">{selectedLog.ipAddress}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">User Agent:</span>
                <span className="detail-value">{selectedLog.userAgent}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Request Body:</span>
                <pre className="detail-code">{JSON.stringify(selectedLog.requestBody, null, 2)}</pre>
              </div>
            </div>
            <div className="detail-section">
              <h4>Response</h4>
              <div className="detail-item">
                <span className="detail-label">Status Code:</span>
                <span className="detail-value">{selectedLog.statusCode}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Response Time:</span>
                <span className="detail-value">{selectedLog.responseTime}ms</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Response Body:</span>
                <pre className="detail-code">{JSON.stringify(selectedLog.responseBody, null, 2)}</pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiLogs;

